import {
  BooleanInput,
  required,
  SelectInput,
  TextInput,
  useTranslate,
  useNotify,
  useGetIdentity,
} from 'react-admin';
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { Resources } from '../resource';
import { useFormContext } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { hotSearchDataProvider } from './dataProvider';
import { UserModel } from '../../entities/user.entity';

interface FormProps {
  isCreate: boolean;
}

interface TokenItem {
  symbol?: string;
  chainId?: number;
  token?: string;
  tokenContract?: string;
  name?: string;
  tokenName?: string;
  price?: string;
  price24hChange?: string;
  marketcap?: string;
}

const HotSearchForm = (props: FormProps) => {
  const { isCreate } = props;
  const t = useTranslate();
  const notify = useNotify();
  const { setValue, watch } = useFormContext();
  const { data: userData } = useGetIdentity();
  const user = userData as UserModel;

  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<TokenItem[]>([]);
  const [searching, setSearching] = useState(false);

  const board = watch('board') || 'CONTRACT';


  // 自动设置 operator 为当前用户名字
  useEffect(() => {
    if (user) {
      const operatorName = user?.name?.trim() || user?.username || user?.email || '';
      if (operatorName) {
        setValue('operator', operatorName, { shouldDirty: true, shouldValidate: false });
      }
    }
  }, [user, setValue]);

  // 搜索 token
  const handleSearch = async () => {
    if (!searchKeyword.trim()) {
      notify(t('module.hot-search.inputSearchKeyword'), { type: 'warning' });
      return;
    }

    setSearching(true);
    try {
      const results = await hotSearchDataProvider.searchTokens(searchKeyword.trim(), board);
      console.log('results === ', results);
      setSearchResults(results || []);
      if (!results || results.length === 0) {
        notify(t('module.hot-search.tokenNotFound'), { type: 'info' });
      }
    } catch (error: any) {
      notify(error.message || t('module.hot-search.searchFailed'), { type: 'error' });
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // 选择 token
  const handleSelectToken = (token: TokenItem) => {
    if (board === 'CONTRACT') {
      // 合约搜索返回的是 symbol, chainId, token, name
      // 使用 shouldDirty: true 确保字段被标记为已修改，这样它们会被包含在提交数据中
      setValue('symbol', token.symbol || '', { shouldDirty: true, shouldValidate: false });
      setValue('chainId', token.chainId?.toString() || '', { shouldDirty: true, shouldValidate: false });
      setValue('tokenContract', token.token || '', { shouldDirty: true, shouldValidate: false });
      setValue('tokenName', token.name || '', { shouldDirty: true, shouldValidate: false });
    } else {
      // MEME 和 USTOCK 搜索返回的是 symbol, chainId, token, name
      setValue('symbol', token.symbol || '', { shouldDirty: true, shouldValidate: false });
      setValue('chainId', token.chainId?.toString() || '', { shouldDirty: true, shouldValidate: false });
      setValue('tokenContract', token.token || '', { shouldDirty: true, shouldValidate: false });
      setValue('tokenName', token.name || '', { shouldDirty: true, shouldValidate: false });
    }
    notify(t('module.hot-search.tokenInfoSet'), { type: 'success' });
  };

  return (
    <>
      <Stack spacing={3} gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.hot-search.board')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <SelectInput
              source="board"
              label={false}
              choices={[
                { id: 'CONTRACT', name: 'CONTRACT' },
                { id: 'MEME', name: 'MEME' },
                { id: 'USTOCK', name: 'USTOCK' },
              ]}
              validate={[required()]}
              fullWidth
              defaultValue="CONTRACT"
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.hot-search.mode')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <SelectInput
              source="mode"
              label={false}
              choices={[
                { id: 'SYSTEM_AUTO', name: 'SYSTEM_AUTO' },
                { id: 'DEFAULT_TEXT', name: 'DEFAULT_TEXT' },
                { id: 'CUSTOM', name: 'CUSTOM' },
              ]}
              validate={[required()]}
              fullWidth
            />
          </Box>
        </Stack>

        {/* 搜索功能 */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.hot-search.searchToken')}
          </Typography>
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('module.hot-search.searchTokenPlaceholder')}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
            <Button variant="outlined" onClick={handleSearch} disabled={searching}>
              {searching ? <CircularProgress size={18} /> : t('module.hot-search.search')}
            </Button>
          </Box>
        </Stack>

        {/* 搜索结果 */}
        {searchResults.length > 0 && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            spacing={2}
            mb={1}
          >
            <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
              {t('module.hot-search.searchResults')}
            </Typography>
            <Box sx={{ flexGrow: 1 }}>
              <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List>
                  {searchResults.map((token, index) => (
                    <ListItem
                      key={index}
                      onClick={() => handleSelectToken(token)}
                      sx={{
                        borderBottom: '1px solid #eee',
                        '&:hover': { backgroundColor: '#f5f5f5' },
                      }}
                    >
                      <ListItemText
                        primary={`${token.symbol || '-'} - ${token.name || token.tokenName || '-'}`}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              Chain ID: {token.chainId || '-'}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ wordBreak: 'break-all' }}
                            >
                              Contract: {token.token || token.tokenContract || '-'}
                            </Typography>
                            {token.price && (
                              <Typography variant="caption" display="block">
                                Price: {token.price}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectToken(token);
                          }}
                        >
                          {t('module.hot-search.select')}
                        </Button>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          </Stack>
        )}

        {/* Token 信息显示 */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.hot-search.symbol')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="symbol"
              placeholder={t('module.hot-search.symbol')}
              fullWidth
              readOnly
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: '#f5f5f5',
                  color: '#9e9e9e',
                },
                '& .MuiInputBase-input': {
                  color: '#9e9e9e',
                },
              }}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.hot-search.chainId')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="chainId"
              placeholder={t('module.hot-search.chainId')}
              fullWidth
              readOnly
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: '#f5f5f5',
                  color: '#9e9e9e',
                },
                '& .MuiInputBase-input': {
                  color: '#9e9e9e',
                },
              }}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.hot-search.tokenContract')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="tokenContract"
              placeholder={t('module.hot-search.tokenContract')}
              fullWidth
              readOnly
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: '#f5f5f5',
                  color: '#9e9e9e',
                },
                '& .MuiInputBase-input': {
                  color: '#9e9e9e',
                },
              }}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.hot-search.tokenName')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="tokenName"
              placeholder={t('module.hot-search.tokenName')}
              fullWidth
              readOnly
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: '#f5f5f5',
                  color: '#9e9e9e',
                },
                '& .MuiInputBase-input': {
                  color: '#9e9e9e',
                },
              }}
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.hot-search.showText')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="displayText"
              placeholder={t('module.hot-search.showTextPlaceholder')}
              fullWidth
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.hot-search.showFlames')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <BooleanInput label="" source="showFlame" defaultValue={false} />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.hot-search.status')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <SelectInput
              source="status"
              label={false}
              choices={[
                { id: 'ACTIVE', name: 'ACTIVE' },
                { id: 'INACTIVE', name: 'INACTIVE' },
              ]}
              validate={[required()]}
              fullWidth
              defaultValue="ACTIVE"
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.hot-search.operator')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="operator"
              placeholder={t('module.hot-search.operator')}
              fullWidth
              readOnly
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: '#f5f5f5',
                  color: '#9e9e9e',
                },
                '& .MuiInputBase-input': {
                  color: '#9e9e9e',
                },
              }}
            />
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

// @ts-ignore
export default HotSearchForm;
